import { useCallback } from 'react';

/**
 * Hook para gerenciar a lógica de drag and drop de serviços e categorias com UI otimista
 * 
 * @param {Object} params - Parâmetros do hook
 * @param {Array} params.services - Lista de serviços do banco
 * @param {Array} params.categories - Lista de categorias do banco
 * @param {Array} params.localServices - Lista de serviços local (estado da UI)
 * @param {Array} params.localCategories - Lista de categorias local (estado da UI)
 * @param {Function} params.setLocalServices - Função para atualizar serviços locais
 * @param {Function} params.setLocalCategories - Função para atualizar categorias locais
 * @param {Function} params.updateServicesOrder - Função para atualizar ordem no banco
 * @param {Function} params.updateCategoriesOrder - Função para atualizar ordem no banco
 * @param {Function} params.updateService - Função para atualizar serviço no banco
 * @param {Function} params.setOperationError - Função para definir erro de operação
 * @param {Function} params.setSaving - Função para indicar operação em andamento (opcional)
 * @returns {Object} - Funções para manipular drag and drop
 */
const useDragAndDrop = ({
  services,
  categories,
  localServices,
  localCategories,
  setLocalServices,
  setLocalCategories,
  updateServicesOrder,
  updateCategoriesOrder,
  updateService,
  setOperationError,
  setSaving
}) => {
  /**
   * Manipula o fim de uma operação de arrastar e soltar com UI otimista
   * @param {Object} result - Resultado da operação de drag and drop
   */
  const handleDragEnd = useCallback((result) => {
    try {
      const { destination, source, type, draggableId } = result;
      
      // Se não tiver destino ou o destino for o mesmo que a origem, não fazer nada
      if (!destination || 
          (destination.droppableId === source.droppableId && 
           destination.index === source.index)) {
        return;
      }
      
      // Indicar que está salvando (se a função for fornecida)
      if (setSaving) setSaving(true);
      
      // Reorganização de categorias
      if (type === 'category') {
        // 1. Atualizar o estado local primeiro para UI otimista
        const validCategories = [...localCategories].filter(category => category && category.id);
        const categoryIds = validCategories.map(category => category.id);
        
        if (source.index >= categoryIds.length) {
          console.error('Índice de origem inválido');
          setOperationError('Erro ao reordenar: índice de origem inválido');
          if (setSaving) setSaving(false);
          return;
        }
        
        const [movedCategoryId] = categoryIds.splice(source.index, 1);
        
        if (!movedCategoryId) {
          console.error('ID da categoria movida é undefined');
          setOperationError('Erro ao reordenar: ID de categoria inválido');
          if (setSaving) setSaving(false);
          return;
        }
        
        categoryIds.splice(destination.index, 0, movedCategoryId);
        
        // Verificar se todos os IDs são válidos
        if (categoryIds.some(id => !id)) {
          console.error('Lista de categorias contém IDs inválidos', categoryIds);
          setOperationError('Erro ao reordenar: IDs de categoria inválidos');
          if (setSaving) setSaving(false);
          return;
        }
        
        // Atualizar estado local primeiro (UI otimista)
        const orderedCategories = categoryIds.map((id, index) => {
          const category = validCategories.find(c => c.id === id);
          return { ...category, ordem: index };
        });
        
        // Preservar categorias que não estão na lista ordenada
        const otherCategories = validCategories.filter(c => !categoryIds.includes(c.id));
        setLocalCategories([...orderedCategories, ...otherCategories]);
        
        // 2. Atualizar banco de dados em segundo plano
        updateCategoriesOrder(categoryIds)
          .catch(err => {
            // Em caso de erro, reverter para o estado original
            console.error('Erro ao atualizar ordem das categorias:', err);
            setOperationError(`Erro ao salvar: ${err.message}. A página será atualizada.`);
            setLocalCategories(categories); // Revertendo para o estado original
          })
          .finally(() => {
            if (setSaving) setSaving(false);
          });
        
        return;
      }
      
      // Reorganização de serviços dentro da mesma categoria
      if (destination.droppableId === source.droppableId) {
        const categoryId = source.droppableId;
        // Filtrar serviços inválidos localmente
        const categoryServices = [...localServices]
          .filter(service => service && service.id && 
            (service.categoria === categoryId || service.categoryId === categoryId))
          .sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
        
        if (categoryServices.length === 0) {
          console.error('Nenhum serviço válido na categoria');
          if (setSaving) setSaving(false);
          return;
        }
        
        if (source.index >= categoryServices.length) {
          console.error('Índice de origem inválido');
          setOperationError('Erro ao reordenar: índice de origem inválido');
          if (setSaving) setSaving(false);
          return;
        }
        
        const serviceIds = categoryServices.map(service => service.id);
        const [movedServiceId] = serviceIds.splice(source.index, 1);
        
        if (!movedServiceId) {
          console.error('ID do serviço movido é undefined');
          setOperationError('Erro ao reordenar: ID de serviço inválido');
          if (setSaving) setSaving(false);
          return;
        }
        
        serviceIds.splice(destination.index, 0, movedServiceId);
        
        // Verificar se todos os IDs são válidos
        if (serviceIds.some(id => !id)) {
          console.error('Lista de serviços contém IDs inválidos', serviceIds);
          setOperationError('Erro ao reordenar: IDs de serviço inválidos');
          if (setSaving) setSaving(false);
          return;
        }
        
        // Atualizar estado local primeiro (UI otimista)
        const updatedServices = [...localServices];
        
        // Atualizar a ordem dos serviços na categoria
        serviceIds.forEach((id, index) => {
          const serviceIndex = updatedServices.findIndex(s => s.id === id);
          if (serviceIndex >= 0) {
            updatedServices[serviceIndex] = {
              ...updatedServices[serviceIndex],
              ordem: index
            };
          }
        });
        
        setLocalServices(updatedServices);
        
        // Atualizar banco de dados em segundo plano
        updateServicesOrder(serviceIds)
          .catch(err => {
            console.error('Erro ao atualizar ordem dos serviços:', err);
            setOperationError(`Erro ao salvar: ${err.message}. A página será atualizada.`);
            setLocalServices(services); // Revertendo para o estado original
          })
          .finally(() => {
            if (setSaving) setSaving(false);
          });
        
        return;
      }
      
      // Reorganização de serviços entre categorias diferentes
      const sourceCategory = source.droppableId;
      const destinationCategory = destination.droppableId;
      
      // Trabalhar com cópias locais para UI otimista
      const updatedServices = [...localServices];
      
      // Filtrar serviços da categoria de origem
      const sourceCategoryServices = updatedServices
        .filter(service => service && service.id && 
          (service.categoria === sourceCategory || service.categoryId === sourceCategory))
        .sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
      
      if (sourceCategoryServices.length === 0) {
        console.error('Nenhum serviço válido na categoria de origem');
        setOperationError('Erro ao mover: categoria de origem sem serviços válidos');
        if (setSaving) setSaving(false);
        return;
      }
      
      if (source.index >= sourceCategoryServices.length) {
        console.error('Índice de origem inválido');
        setOperationError('Erro ao mover: índice de origem inválido');
        if (setSaving) setSaving(false);
        return;
      }
      
      // Obter o serviço que foi movido
      const movedService = sourceCategoryServices[source.index];
      
      if (!movedService || !movedService.id) {
        console.error('Serviço movido é undefined ou não tem ID');
        setOperationError('Erro ao mover: serviço inválido');
        if (setSaving) setSaving(false);
        return;
      }
      
      // Filtrar serviços da categoria de destino
      const destinationCategoryServices = updatedServices
        .filter(service => service && service.id && 
          (service.categoria === destinationCategory || service.categoryId === destinationCategory))
        .sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
      
      // Encontrar o serviço no array atualizado e atualizar sua categoria
      const serviceIndex = updatedServices.findIndex(s => s.id === movedService.id);
      if (serviceIndex >= 0) {
        // Atualizar o serviço com a nova categoria localmente
        updatedServices[serviceIndex] = {
          ...updatedServices[serviceIndex],
          categoria: destinationCategory,
          categoryId: destinationCategory
        };
      }
      
      // Reorganizar os serviços de destino localmente
      const destinationServiceIds = destinationCategoryServices.map(service => service.id);
      destinationServiceIds.splice(destination.index, 0, movedService.id);
      
      // Atualizar a ordem de todos os serviços na categoria de destino
      destinationServiceIds.forEach((id, index) => {
        const serviceIdx = updatedServices.findIndex(s => s.id === id);
        if (serviceIdx >= 0) {
          updatedServices[serviceIdx] = {
            ...updatedServices[serviceIdx],
            ordem: index
          };
        }
      });
      
      // Atualizar estado local para UI otimista
      setLocalServices(updatedServices);
      
      // Backup para possível reversão
      const originalServices = [...services];
      
      // Atualizar a categoria do serviço no banco em segundo plano
      updateService(movedService.id, { 
        ...movedService, 
        categoria: destinationCategory,
        categoryId: destinationCategory
      })
        .then(() => {
          // Após sucesso da primeira operação, reordenar os serviços
          return updateServicesOrder(destinationServiceIds);
        })
        .catch(err => {
          console.error('Erro ao mover serviço entre categorias:', err);
          setOperationError(`Erro ao salvar: ${err.message}. A página será atualizada.`);
          setLocalServices(originalServices); // Revertendo para o estado original
        })
        .finally(() => {
          if (setSaving) setSaving(false);
        });
        
    } catch (err) {
      console.error('Erro ao processar drag and drop:', err);
      setOperationError(`Erro ao processar operação: ${err.message}`);
      if (setSaving) setSaving(false);
    }
  }, [
    localCategories, 
    localServices, 
    categories,
    services,
    setLocalCategories,
    setLocalServices,
    updateService, 
    updateServicesOrder, 
    updateCategoriesOrder, 
    setOperationError,
    setSaving
  ]);

  return {
    handleDragEnd
  };
};

export default useDragAndDrop; 